import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { Modal, Box, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import "swiper/css";
import "swiper/css/pagination";
import "./EarnedBadges.css";
import { useDispatch, useSelector } from "react-redux";
import { getCompletedBadgeQuizAction } from "../../../actions/Badge/badgeActions";
import MySkeletonLoader from "../reusables/MySkeletonLoader";
import { APIRequestURI } from "../../../utils/api_request_uri";

const testimonials = [
  {
    name: "Rohit Kumar",
    role: "Software Engineer",
    company: "TCS",
    profileImage:
      "https://ui-avatars.com/api/?name=Rohit+Kumar&background=0D8ABC&color=fff",
    text: "After adding my AceTechies certification badges to LinkedIn, I got 5x more profile views and 3 recruiter messages in the first week!",
    timestamp: "1w",
    likes: 156,
  },
  {
    name: "Neha Kapoor",
    role: "Frontend Developer",
    company: "Infosys",
    profileImage:
      "https://ui-avatars.com/api/?name=Neha+Kapoor&background=0D8ABC&color=fff",
    text: "The badges made my LinkedIn profile stand out! Recruiters specifically mentioned my certifications during interview calls.",
    timestamp: "2w",
    likes: 203,
  },
  {
    name: "Arjun Reddy",
    role: "Full Stack Developer",
    company: "Wipro",
    profileImage:
      "https://ui-avatars.com/api/?name=Arjun+Reddy&background=0D8ABC&color=fff",
    text: "My certification badges helped me transition to a senior role. HR teams trust verified credentials from AceTechies!",
    timestamp: "3w",
    likes: 189,
  },
  {
    name: "Kavya Iyer",
    role: "Backend Engineer",
    company: "HCL Technologies",
    profileImage:
      "https://ui-avatars.com/api/?name=Kavya+Iyer&background=0D8ABC&color=fff",
    text: "Adding badges to my profile increased my credibility. Got interview calls from 4 companies within 2 weeks of posting!",
    timestamp: "1mo",
    likes: 178,
  },
  {
    name: "Siddharth Joshi",
    role: "DevOps Specialist",
    company: "Tech Mahindra",
    profileImage:
      "https://ui-avatars.com/api/?name=Siddharth+Joshi&background=0D8ABC&color=fff",
    text: "These certification badges are game-changers! LinkedIn engagement doubled and I'm now getting better job opportunities.",
    timestamp: "1mo",
    likes: 234,
  },
  {
    name: "Meera Nair",
    role: "Cloud Engineer",
    company: "Accenture",
    profileImage:
      "https://ui-avatars.com/api/?name=Meera+Nair&background=0D8ABC&color=fff",
    text: "Displaying my AceTechies badges on LinkedIn helped me land my dream job. Recruiters love verified certifications!",
    timestamp: "2mo",
    likes: 267,
  },
];

export default function EarnedBadges() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);

  const { result, loading } = useSelector(
    (state) => state.getDashboardCompletedStore,
  );

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getCompletedBadgeQuizAction());
  }, [dispatch]);

  // Transform the API data into the format needed for display
  const badges =
    result?.data?.map((item) => ({
      id: item._id,
      image: item.badgeAwarded?.imageUrl || "",
      title: item.quiz?.title || "Badge",
      tag: item.badgeAwarded?.title || "Certified",
    })) || [];

  const hasBadges = badges.length > 0;

  const handleCardClick = (badge) => {
    setSelectedBadge(badge);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedBadge(null);
  };

  const handleDownload = (imageUrl, title) => {
    const BASE_URL = new APIRequestURI().getBaseURI();

    const downloadUrl =
      `${BASE_URL}/api/badges/download` +
      `?url=${encodeURIComponent(imageUrl)}` +
      `&title=${encodeURIComponent(title)}`;

    window.location.href = downloadUrl;
  };

  if (loading) {
    return (
      <div className="earned_badges_panel">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="earned_badges_header"
        >
          <h2>Earned Badges</h2>
        </motion.div>
        <div style={{ display: "flex" }}>
          {[1, 2, 3].map((i) => (
            <MySkeletonLoader />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="earned_badges_panel">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="earned_badges_header"
      >
        <h2>Earned Badges</h2>
      </motion.div>

      {hasBadges ? (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="earned_badges_grid"
          >
            {badges.map((badge, index) => (
              <motion.div
                key={badge.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="earned_badges_card"
                onClick={() => handleCardClick(badge)}
              >
                <span className="earned_badges_tag">{badge.tag}</span>
                <div className="earned_badges_image_container">
                  <img
                    src={badge.image}
                    alt={badge.title}
                    className="earned_badges_image"
                  />
                </div>
                <h3 className="earned_badges_title">{badge.title}</h3>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  className="earned_badges_download_button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(badge.image, badge.title);
                  }}
                >
                  Download
                </Button>
              </motion.div>
            ))}
          </motion.div>

          {/* Social Proof Section */}
          <div className="earned_badges_social_proof">
            <h3>How badges boosted careers on LinkedIn</h3>

            <div className="earned_badges_testimonials_slider_container">
              <Swiper
                modules={[Autoplay, Pagination]}
                spaceBetween={16}
                slidesPerView={1}
                autoplay={{ delay: 3500, disableOnInteraction: false }}
                pagination={{ clickable: true }}
                loop={true}
                breakpoints={{
                  640: { slidesPerView: 1.5 },
                  768: { slidesPerView: 2 },
                  1024: { slidesPerView: 2.5 },
                  1280: { slidesPerView: 3 },
                }}
              >
                {testimonials.map((item, index) => (
                  <SwiperSlide key={index}>
                    <motion.div
                      whileHover={{ scale: 1.02, y: -4 }}
                      transition={{ duration: 0.2 }}
                      className="earned_badges_linkedin_post_card"
                    >
                      {/* Header */}
                      <div className="earned_badges_post_header">
                        <img
                          src={item.profileImage}
                          alt={item.name}
                          className="earned_badges_profile_avatar"
                        />
                        <div className="earned_badges_profile_info">
                          <h4 className="earned_badges_profile_name">
                            {item.name}
                          </h4>
                          <p className="earned_badges_profile_role">
                            {item.role}
                          </p>
                          <p className="earned_badges_profile_meta">
                            {item.company} • {item.timestamp}
                          </p>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="earned_badges_post_content">
                        <p>{item.text}</p>
                      </div>

                      {/* Footer */}
                      <div className="earned_badges_post_footer">
                        <div className="earned_badges_post_engagement">
                          <span className="earned_badges_engagement_icon">
                            👍
                          </span>
                          <span className="earned_badges_engagement_count">
                            {item.likes}
                          </span>
                        </div>
                      </div>

                      {/* LinkedIn-style border top */}
                      <div className="earned_badges_linkedin_accent"></div>
                    </motion.div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="earned_badges_empty"
        >
          <div className="earned_badges_empty_icon">🏆</div>
          <p className="earned_badges_empty_title">No badges earned yet</p>
          <p className="earned_badges_empty_subtitle">
            Complete certifications and challenges to earn your first badge and
            showcase your skills!
          </p>

          <Button className="earned_badges_get_badge_button">
            Get Your Badge Now
          </Button>
        </motion.div>
      )}

      {/* Modal */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="badge-modal-title"
        aria-describedby="badge-modal-description"
      >
        <Box className="earned_badges_modal_box">
          <IconButton
            className="earned_badges_modal_close"
            onClick={handleCloseModal}
          >
            <CloseIcon />
          </IconButton>

          {selectedBadge && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="earned_badges_modal_content"
            >
              <img
                src={selectedBadge.image}
                alt={selectedBadge.title}
                className="earned_badges_modal_image"
              />
              <h2 className="earned_badges_modal_title">
                {selectedBadge.title}
              </h2>
              <span className="earned_badges_modal_tag">
                {selectedBadge.tag}
              </span>

              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                className="earned_badges_modal_download_button"
                onClick={() =>
                  handleDownload(selectedBadge.image, selectedBadge.title)
                }
              >
                Download Badge
              </Button>
            </motion.div>
          )}
        </Box>
      </Modal>
    </div>
  );
}
